"use client";
import React, { useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Tabs, Divider, Button, Flex } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import Paragraph from "antd/lib/typography/Paragraph";
import Text from "antd/lib/typography/Text";
import PageLoading from "@/app/components/PageLoading";
import { buildCatalogServiceHub } from "@/consts/serviceDetails";
import { toEditionBusinessKey } from "@/consts/businesses";
import type { BusinessService, BusinessServiceCategory } from "@/lib/api/types";
import "./ServicesList.scss";

const groupServices = (services: BusinessService[]) => {
  const groups = new Map<
    string,
    { category?: BusinessServiceCategory; services: BusinessService[] }
  >();

  for (const service of services) {
    const key = service.category?.id ?? "uncategorized";
    const group = groups.get(key) ?? { category: service.category, services: [] };
    group.services.push(service);
    groups.set(key, group);
  }

  return [...groups.values()].sort((a, b) => {
    if (!a.category) return b.category ? 1 : 0;
    if (!b.category) return -1;
    return a.category.sortOrder - b.category.sortOrder;
  });
};

interface ServicesListProps {
  initialServices: BusinessService[];
  /** Brand shown first (server-rendered). Defaults to Eddie's Edition. */
  initialBusiness?: string;
}

const BUSINESS_TABS = [
  { key: "eddies", label: "Eddie's Edition" },
  { key: "edition-studio", label: "Edition Studio" },
];

const ServiceTitle = ({ title, href }: { title: string; href?: string }) => {
  const label = <Text strong>{title}</Text>;
  return href ? <Link href={href}>{label}</Link> : label;
};

const ServicesList: React.FC<ServicesListProps> = ({
  initialServices,
  initialBusiness = "eddies",
}) => {
  const router = useRouter();
  const [loading, startTransition] = useTransition();
  // The server resolves both from the URL. Keeping a second client copy lets
  // browser Back restore a catalog that no longer matches the address bar.
  const business = toEditionBusinessKey(initialBusiness);
  const services = initialServices;
  const serviceGroups = groupServices(services);
  const hasCategories = serviceGroups.some((group) => group.category);

  // Eddie's Edition spans multiple locations, so send guests to the location
  // browser. Edition Studio is a single location with a dedicated booking page.
  const appointmentHref =
    business === "edition-studio"
      ? "/appointment/edition-studio"
      : "/appointment";

  const handleBusinessChange = (nextBusiness: string) => {
    const url = new URL(window.location.href);
    if (nextBusiness === "edition-studio") url.searchParams.set("business", nextBusiness);
    else url.searchParams.delete("business");
    startTransition(() => {
      router.replace(`${url.pathname}${url.search}${url.hash}`, { scroll: false });
    });
  };

  return (
    <>
      <Tabs
        activeKey={business}
        items={BUSINESS_TABS}
        onChange={handleBusinessChange}
        tabBarStyle={{ marginBottom: 24 }}
      />

      {loading ? (
        <PageLoading height={"64px"} />
      ) : services.length === 0 ? (
        <Paragraph type="secondary">
          No services are available right now.
        </Paragraph>
      ) : (
        <Flex vertical gap={16}>
          <div className="services-list__groups">
            {serviceGroups.map((group) => (
              <section
                key={group.category?.id ?? "uncategorized"}
                aria-labelledby={
                  hasCategories
                    ? `service-category-${group.category?.id ?? "other"}`
                    : undefined
                }
              >
                {hasCategories && (
                  <h2
                    id={`service-category-${group.category?.id ?? "other"}`}
                    className="t-section services-list__category-title"
                  >
                    {group.category?.name ?? "Other services"}
                  </h2>
                )}
                {group.services.map((service) => {
                  const serviceHub = buildCatalogServiceHub(service, toEditionBusinessKey(business));
                  const serviceHubPath = serviceHub?.path;
                  const description =
                    serviceHub?.listDescription ?? service.description;
                  const priceLabel =
                    serviceHub?.listPriceLabel ?? `$${service.price} +`;

                  return (
                    <section className="services-list__item" key={service.title}>
                      <Paragraph className="services-list__details">
                        <ServiceTitle
                          title={service.title}
                          href={serviceHubPath}
                        />{" "}
                        <Divider type="vertical" />
                        <Text>{priceLabel}</Text>
                        {description && (
                          <>
                            <br />
                            <Text type="secondary">{description}</Text>
                          </>
                        )}
                      </Paragraph>
                    </section>
                  );
                })}
              </section>
            ))}
          </div>

          <footer>
            <Button
              href={appointmentHref}
              color="default"
              variant="solid"
              icon={<CalendarOutlined />}
            >
              Make an Appointment
            </Button>
          </footer>
        </Flex>
      )}
    </>
  );
};

export default ServicesList;
